import BaseInput from '@components/v2/form/BaseInput/BaseInput';
import Icon from '@components/v2/ui/Icon/Icon';
import MoreActionsButton from '@components/v2/ui/MoreActionsButton/MoreActionsButton';
import Typography from '@components/v2/ui/Typography/Typography';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Stack } from '@mui/material';
import { Ellipsis, GripVertical, Pencil, Trash2 } from 'lucide-react';
import { KeyboardEvent, MouseEvent, PointerEvent, useRef, useState } from 'react';
import LangEnum from '../../../../../../lang/LangEnum';
import { CatalogProductsTestIds } from '../../../../../../utils/testIds/CatalogProducts.testIds';
import { useTranslation } from '../../../../../translation/TranslationContext';
import { ROW_HEIGHT_PX } from './catalogSectionsDropdown.constants';
import { CatalogSectionRowProps } from './catalogSectionsDropdown.types';

const CLICK_TOLERANCE_PX = 5;

interface ResolveRowCursorParams {
  isDragging: boolean;
  isSelectable: boolean;
}

const resolveRowCursor = ({ isDragging, isSelectable }: ResolveRowCursorParams) => {
  if (isDragging) {
    return 'grabbing';
  }

  if (isSelectable) {
    return 'pointer';
  }

  return undefined;
};

export const CatalogSectionRow = ({
  entry,
  isEditing,
  canManage,
  onStartEdit,
  onCommitEdit,
  onCancelEdit,
  onRemove,
  onSelect,
}: CatalogSectionRowProps) => {
  const { translate } = useTranslation();
  const pointerOrigin = useRef<{ x: number; y: number } | null>(null);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: entry.key,
    disabled: isEditing || !canManage,
  });

  const selectableId = isEditing ? undefined : entry.id;

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    pointerOrigin.current = { x: event.clientX, y: event.clientY };
    listeners?.onPointerDown?.(event);
  };

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    const origin = pointerOrigin.current;
    pointerOrigin.current = null;

    if (!selectableId || !origin) {
      return;
    }

    const hasMoved =
      Math.abs(event.clientX - origin.x) > CLICK_TOLERANCE_PX ||
      Math.abs(event.clientY - origin.y) > CLICK_TOLERANCE_PX;

    if (hasMoved) {
      return;
    }

    onSelect(selectableId);
  };

  return (
    <Stack
      direction="row"
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      test-id={CatalogProductsTestIds.SECTIONS_DROPDOWN_ROW}
      data-row-key={entry.key}
      title={
        canManage && entry.isDefault
          ? translate(LangEnum.CATALOGS_SECTIONS_DEFAULT_NOT_EDITABLE)
          : undefined
      }
      sx={(theme) => ({
        alignItems: 'center',
        gap: 1,
        minHeight: `${ROW_HEIGHT_PX}px`,
        paddingX: 0.75,
        borderRadius: `${theme.shape.borderRadiusMd}px`,
        backgroundColor: isEditing ? theme.palette.action.selected : 'transparent',
        touchAction: 'none',
        opacity: isDragging ? 0.5 : 1,
        cursor: resolveRowCursor({ isDragging, isSelectable: Boolean(selectableId) }),
        '&:hover': selectableId ? { backgroundColor: theme.palette.action.hover } : undefined,
      })}
    >
      {canManage && (
        <Box
          test-id={CatalogProductsTestIds.SECTIONS_DROPDOWN_ROW_GRIP}
          aria-label={translate(LangEnum.CATALOGS_SECTIONS_REORDER_HANDLE)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            cursor: isDragging ? 'grabbing' : 'grab',
          }}
        >
          <Icon icon={GripVertical} size="xs" />
        </Box>
      )}

      {isEditing ? (
        <SectionNameInput
          initialName={entry.name}
          onCommit={onCommitEdit}
          onCancel={onCancelEdit}
        />
      ) : (
        <Typography
          variant="body1"
          noWrap
          sx={{ flex: 1, minWidth: 0, cursor: 'text', userSelect: 'text' }}
          testId={CatalogProductsTestIds.SECTIONS_DROPDOWN_ROW_NAME}
        >
          {entry.name}
        </Typography>
      )}

      {canManage && !entry.isDefault && !isEditing && (
        <Box
          sx={{ display: 'inline-flex' }}
          onClick={(event) => event.stopPropagation()}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <MoreActionsButton
            testId={CatalogProductsTestIds.SECTIONS_DROPDOWN_ROW_ACTIONS}
            trigger={{
              variant: 'icon',
              icon: Ellipsis,
              ariaLabel: LangEnum.CATALOGS_SECTIONS_ROW_ACTIONS,
            }}
            actions={[
              {
                label: LangEnum.CATALOGS_SECTIONS_RENAME,
                icon: Pencil,
                testId: CatalogProductsTestIds.SECTIONS_DROPDOWN_ROW_EDIT,
                onClick: () => onStartEdit(entry.key),
              },
              {
                label: LangEnum.CATALOGS_SECTIONS_DELETE,
                icon: Trash2,
                testId: CatalogProductsTestIds.SECTIONS_DROPDOWN_ROW_DELETE,
                onClick: () => onRemove(entry.key),
              },
            ]}
          />
        </Box>
      )}
    </Stack>
  );
};

interface SectionNameInputProps {
  initialName: string;
  onCommit: (name: string) => void;
  onCancel: () => void;
}

const SectionNameInput = ({ initialName, onCommit, onCancel }: SectionNameInputProps) => {
  const { translate } = useTranslation();
  const [value, setValue] = useState(initialName);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onCommit(value);
      return;
    }
    if (event.key === 'Escape') {
      event.stopPropagation();
      onCancel();
    }
  };

  return (
    <BaseInput
      autoFocus
      value={value}
      testId={CatalogProductsTestIds.SECTIONS_DROPDOWN_ROW_INPUT}
      ariaLabel={translate(LangEnum.CATALOGS_SECTIONS_RENAME)}
      sx={(theme) => ({ flex: 1, minWidth: 0, ...theme.typography.body1 })}
      onChange={(event) => setValue(event.target.value)}
      onKeyDown={handleKeyDown}
      onBlur={() => onCommit(value)}
    />
  );
};
